import React, { useEffect, useMemo, useState } from "react";

import { ServiceType } from "../../models/setup/service-type";

import BreadCrumb from "common/breadcrumb";
import LoadingComponent from "common/components/loader";
import TableContainer from "common/table-container";

//
import { CreateServiceType, UpdateServiceType, GetServiceTypes } from "api/setup/index";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { Pencil, X } from "lucide-react";

const ServiceTypePage: React.FC = () => {
  const title = "Service Types";
  const pageTitle = "Setup";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ServiceType[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selected, setSelected] = useState<ServiceType | undefined>();
  const [editMode, setEditMode] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    //param?: TenantSearchParam
    setLoading(true);
    const res = await GetServiceTypes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.data?.length ?? 0);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleEditClick = (id: number) => {
    const _edited = data.find((d) => d.id == id);
    setSelected(_edited);
    setEditMode(true);
    formik.setValues(_edited); // Set formik values when prop changes
  };
  const handleResetClick = () => {
    setSelected(undefined);
    setEditMode(false);
    formik.setValues({
      name: "",
      isActive: true,
    }); // Set formik values when prop changes
  };
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { isActive, id } = cell.row.original;

          return (
            <div className="flex items-center">
              <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                <input
                  type="checkbox"
                  id={`status-${id}`} // unique ID for each row
                  checked={isActive}
                  readOnly // making checkbox read-only to avoid user interaction
                  className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                />
                <label
                  htmlFor={`status-${id}`}
                  className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer transition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                ></label>
              </div>
            </div>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleEditClick(cell.row.original.id)}
            >
              <Pencil className="size-4" />
            </div>
          </div>
        ),
      },
    ],
    [data]
  );

  //Add

  const formik: any = useFormik({
    initialValues: {
      name: "",
      isActive: true,
      ...selected, // Spread the fetched selected  data if available
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Name is required"),
    }),

    onSubmit: async (values) => {
      try {
        setError("");
        setSuccess("");
        setSaving(true);

        var _object: ServiceType = {
          id: selected?.id ?? undefined,
          name: values.name ?? "",
          isActive: values.isActive ?? true,
        };
        const res = (selected?.id ?? 0) == 0 ? await CreateServiceType(_object) : await UpdateServiceType(_object);

        if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
          setSuccess("success");
          setSelected(undefined);
          loadData();
          formik.resetForm();
          // setEditMode(true);
        } else {
          setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
        }
        setSaving(false);
      } catch (error) {
        setError("Something went wrong...");
        setSaving(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
        <div className="xl:col-span-6">
          <div className="card">
            <div className="card-body">
              <h6 className="mb-4 text-15"> {(selected?.id ?? "") == "" ? "Add" : "Update"} Service Type</h6>

              <form
                action="#!"
                id="serviceTypeForm"
                onSubmit={(event: any) => {
                  event.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <>
                  <div className="grid grid-cols-1 gap-x-5">
                    <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-1">
                      <div className="mb-4">
                        <label htmlFor="nameInput" className="inline-block mb-2 text-base font-medium">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="First Name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <p id="name" className="mt-1 text-sm text-red-500">
                            {formik.errors.name}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4">
                        <div>
                          <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                            Active
                          </label>
                          <div className="flex items-center">
                            <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                              <input
                                type="checkbox"
                                name="isActive"
                                id="isActive"
                                className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                                onChange={formik.handleChange}
                                checked={formik.values.isActive}
                              />
                              <label
                                htmlFor="isActive"
                                className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                              ></label>
                            </div>
                          </div>
                        </div>

                        {formik.touched.isActive && formik.errors.isActive ? (
                          <p id="isActive" className="mt-1 text-sm text-red-500">
                            {formik.errors.isActive}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 mt-5">
                    <button
                      type="button"
                      onClick={handleResetClick}
                      className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                    >
                      <X className="inline-block size-4" />
                      <span className="align-middle">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    >
                      {(selected?.id ?? "") == "" ? "Create" : "Update"}
                    </button>
                  </div>
                </>
              </form>
              {saving && <LoadingComponent />}
              {success && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                  id="successAlert"
                >
                  <b>Success! </b>
                  {<>Service type has been {!editMode ? "added" : "updated"} successfully</>}
                </div>
              )}
              {error && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                  id="successAlert"
                >
                  <>
                    Oops! Something went wrong while attempting to {!editMode ? "add" : "update"} service type. Please
                    try again later or contact support for assistance.
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="xl:col-span-6">
          <div className="card">
            <div className="card-body">
              {/*   <h6 className="mb-4 text-15">Service Type</h6> */}
              <div className="mx-auto w-full">
                {loading ? (
                  <LoadingComponent></LoadingComponent>
                ) : (
                  <div className="overflow-x-auto mt-2">
                    {data && data.length > 0 ? (
                      <TableContainer
                        isPagination={true}
                        columns={columns || []}
                        data={data || []}
                        totalCount={total}
                        customPageSize={pageSize}
                        divclassName="overflow-x-auto"
                        tableclassName="w-full whitespace-nowrap"
                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                        thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                        tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                      />
                    ) : (
                      <div className="noresult">
                        <div className="py-6 text-center">
                          {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                          <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                          <p className="mb-0 text-slate-500 dark:text-zink-200">
                            No service types setup, please create new service type.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default ServiceTypePage;
